import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select as MuiSelect,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import capitalize from 'lodash/capitalize';
import isEqual from 'lodash/isEqual';
import { connect } from 'react-redux';
import Select from 'react-select';
import moment from 'moment';

import { getSelectIntervals, getPrice } from './timetable.lib';
import { workingHoursTypeEnum, workingHourPartType, sportsEnum, sportFlagsToKeys } from './../enums';
import { formatPeriod, getMomentFromInterval } from '../lib/utils';
import { sportFlagsToKeysArray, sportKeysToFlagsArray } from '../lib/sportUtils';
import api from '../lib/api';
import { formatBookingErrorForUser } from '../lib/bookingErrors';
import { normalizeListResponse } from '../lib/sectionClientsAggregate';
import { mapOrgClientsToPickerOptions, organizationIdFrom } from '../lib/orgClientsPicker';
import ComplexClientPicker from './sections/ComplexClientPicker';

function getInitialState(props) {
  // Используем workingHoursSports для текущего типа, если доступно, иначе fallback на sport
  const sportsForType = props.workingHoursSports?.[props.currentWorkingHoursTypes] || props.sport || [];
  return {
    sportOptions: getSportOptions(sportsForType),
    beforeIntervals: [],
    afterIntervals: [],
    booking: cleanBooking,
    availableRentalItems: [],
    loadingRentalItems: false,
    isRecurring: false,
    recurringEndDate: moment().add(1, 'month').format('YYYY-MM-DD'),
    recurringDayOfWeek: moment().day(),
    recurringInterval: 1,
    orgClientOptions: [],
    organizationId: null,
    orgClientsLoading: false,
    clientKey: '',
    clientUserId: null,
  };
}

function getSportOptions(sport = []) {
  // Всегда используем sportsEnum из импорта
  if (!sport || sport.length === 0) {
    return [];
  }
  
  // Преобразуем числовые флаги в строковые ключи, если нужно
  let sportKeys = sport;
  if (sport.length > 0 && typeof sport[0] === 'number') {
    sportKeys = sportFlagsToKeysArray(sport);
  }
  
  // Если после преобразования массив пуст, возвращаем пустой массив
  if (!sportKeys || sportKeys.length === 0) {
    return [];
  }
  
  return sportKeys
    .filter(sportItem => {
      // Проверяем, что это строка и существует в sportsEnum
      return sportItem && typeof sportItem === 'string' && sportsEnum[sportItem];
    })
    .map(sportItem => ({
      value: sportItem,
      label: capitalize(sportsEnum[sportItem] || sportItem),
    }));
}

const cleanBooking = {
  intervalData: null,
  startInterval: null, // при выборе часа появляется модальное окно
  endInterval: null,
  choosenDay: null,
  details: {
    user: '',
    phone: '',
    comment: '', // при бронировании даты и времени модератор может оставить сообщение
  },
  rentalItems: [], // Выбранные услуги/инвентарь
};

class BookingDialog extends Component {
  static propTypes = {
    intervalData: PropTypes.object,
    show: PropTypes.bool.isRequired,
    choosenDay: PropTypes.string,
    classes: PropTypes.object,
    currentWorkingHoursTypes: PropTypes.string.isRequired,
    chosenPart: PropTypes.string,
    sports: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
    sport: PropTypes.array.isRequired,
    deleteBooking: PropTypes.func.isRequired,
    schedule: PropTypes.object.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    workingHoursSports: PropTypes.object,
    selectedRange: PropTypes.object, // { startInterval, endInterval, part }
    playgroundId: PropTypes.number,
    sportComplexId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  };

  static defaultProps = {
    intervalData: null,
    choosenDay: '',
    classes: {},
    chosenPart: '',
  };

  state = getInitialState(this.props);

  componentDidMount() {
    if (this.props.intervalData) {
      this.setBooking();
    }
    if (this.props.playgroundId) {
      this.loadAvailableRentalItems();
    }
    this.loadOrgClients();
    // Устанавливаем день недели из выбранной даты
    if (this.props.choosenDay) {
      const dayOfWeek = moment(this.props.choosenDay).day();
      this.setState({ recurringDayOfWeek: dayOfWeek });
    }
  }

  componentDidUpdate(prevProps) {
    // Обновляем день недели при изменении выбранной даты
    if (this.props.choosenDay && this.props.choosenDay !== prevProps.choosenDay) {
      const dayOfWeek = moment(this.props.choosenDay).day();
      this.setState({ recurringDayOfWeek: dayOfWeek });
    }
    // Обновляем опции спорта при изменении sport или workingHoursSports
    const sportChanged = !isEqual(prevProps.sport, this.props.sport);
    const workingHoursSportsChanged = !isEqual(
      prevProps.workingHoursSports?.[prevProps.currentWorkingHoursTypes],
      this.props.workingHoursSports?.[this.props.currentWorkingHoursTypes]
    );
    const currentWorkingHoursTypesChanged = prevProps.currentWorkingHoursTypes !== this.props.currentWorkingHoursTypes;
    
    if (sportChanged || workingHoursSportsChanged || currentWorkingHoursTypesChanged) {
      // eslint-disable-next-line react/no-did-update-set-state
      const sportsForType = this.props.workingHoursSports?.[this.props.currentWorkingHoursTypes] || this.props.sport || [];
      this.setState({
        sportOptions: getSportOptions(sportsForType),
      });
    }
    if (!isEqual(prevProps.intervalData, this.props.intervalData) && this.props.intervalData) {
      this.setBooking();
    }
    if (prevProps.playgroundId !== this.props.playgroundId && this.props.playgroundId) {
      this.loadAvailableRentalItems();
    }
    if (prevProps.sportComplexId !== this.props.sportComplexId && this.props.sportComplexId) {
      this.loadOrgClients();
    }
  }

  loadOrgClients = async () => {
    const complexId = Number(this.props.sportComplexId);
    if (!complexId) {
      this.setState({ orgClientOptions: [], organizationId: null });
      return;
    }
    this.setState({ orgClientsLoading: true });
    try {
      const org = await api.getOrganizationByComplex(complexId);
      const orgId = organizationIdFrom(org);
      if (!orgId) {
        this.setState({ orgClientOptions: [], organizationId: null, orgClientsLoading: false });
        return;
      }
      let items = [];
      try {
        items = normalizeListResponse(await api.getOrganizationClientsPicker(orgId));
      } catch (e) {
        items = [];
      }
      if (!items.length) {
        items = normalizeListResponse(await api.getOrganizationClients(orgId));
      }
      this.setState({
        organizationId: orgId,
        orgClientOptions: mapOrgClientsToPickerOptions(items),
        orgClientsLoading: false,
      });
    } catch (e) {
      this.setState({ orgClientOptions: [], organizationId: null, orgClientsLoading: false });
    }
  };

  loadAvailableRentalItems = async () => {
    if (!this.props.playgroundId) {
      console.log('🔍 [BookingDialog] playgroundId не передан, пропускаем загрузку услуг');
      return;
    }
    
    try {
      console.log('🔍 [BookingDialog] Загрузка услуг для площадки:', this.props.playgroundId, 'дата:', this.props.choosenDay);
      this.setState({ loadingRentalItems: true });
      const items = await api.getAvailableRentalItems(this.props.playgroundId, this.props.choosenDay);
      console.log('🔍 [BookingDialog] Получено услуг из API:', items);
      // Фильтруем только услуги, привязанные к бронированию (ItemType = 0)
      const attachedItems = (items || []).filter(item => item.itemType === 0 || item.itemType === 'AttachedToBooking' || item.itemType === 'attached_to_booking');
      console.log('🔍 [BookingDialog] Отфильтрованные услуги (AttachedToBooking):', attachedItems);
      this.setState({ availableRentalItems: attachedItems });
    } catch (error) {
      console.error('❌ [BookingDialog] Ошибка загрузки услуг:', error);
      this.setState({ availableRentalItems: [] });
    } finally {
      this.setState({ loadingRentalItems: false });
    }
  }

  /**
   * Если изменили селектор "С" и "После",
   * то должны пересчитать сумму и варианты для выбора селекторов
   *  */
  onBookingDataChanged = () => {
    const basePrice = getPrice(this.state.booking, this.props.schedule);
    const rentalItemsPrice = this.calculateRentalItemsPrice();
    this.setBookingData('price', basePrice + rentalItemsPrice);
    this.setSelectIntervals();
  }

  calculateRentalItemsPrice = () => {
    const { booking, availableRentalItems } = this.state;
    if (!booking.rentalItems || booking.rentalItems.length === 0) {
      return 0;
    }

    let total = 0;
    const { startInterval, endInterval } = booking;
    const hoursCount = startInterval && endInterval ? (endInterval - startInterval) / 2 : 0;

    booking.rentalItems.forEach(selectedItem => {
      const item = availableRentalItems.find(i => i.id === selectedItem.id);
      if (!item) return;

      if (item.pricingType === 0) {
        // Фиксированная стоимость
        total += item.price * (selectedItem.quantity || 1);
      } else if (item.pricingType === 1) {
        // За час
        total += item.price * hoursCount * (selectedItem.quantity || 1);
      }
    });

    return total;
  }

  handleRentalItemToggle = (itemId, checked) => {
    this.setState(prevState => {
      const currentItems = prevState.booking.rentalItems || [];
      let newItems;

      if (checked) {
        // Добавляем услугу
        if (!currentItems.find(i => i.id === itemId)) {
          newItems = [...currentItems, { id: itemId, quantity: 1 }];
        } else {
          newItems = currentItems;
        }
      } else {
        // Удаляем услугу
        newItems = currentItems.filter(i => i.id !== itemId);
      }

      return {
        booking: {
          ...prevState.booking,
          rentalItems: newItems,
        },
      };
    }, this.onBookingDataChanged);
  }

  handleRentalItemQuantityChange = (itemId, quantity) => {
    this.setState(prevState => {
      const currentItems = prevState.booking.rentalItems || [];
      const newItems = currentItems.map(item =>
        item.id === itemId ? { ...item, quantity: Math.max(1, parseInt(quantity, 10) || 1) } : item
      );

      return {
        booking: {
          ...prevState.booking,
          rentalItems: newItems,
        },
      };
    }, this.onBookingDataChanged);
  }

  onCancel = () => {
    this.setState({ 
      booking: cleanBooking,
      isRecurring: false,
      recurringEndDate: moment().add(1, 'month').format('YYYY-MM-DD'),
      recurringDayOfWeek: moment().day(),
      recurringInterval: 1,
    });
    this.props.onCancel();
  }

  handleSave = async () => {
    const { isRecurring, recurringEndDate, recurringDayOfWeek, recurringInterval, booking } = this.state;
    const { playgroundId, currentWorkingHoursTypes, choosenDay } = this.props;

    if (isRecurring) {
      // Создаем повторяющиеся бронирования
      // Проверяем, что время выбрано в селекторах "С" и "До"
      console.log('🔍 [BookingDialog] Проверка для повторяющихся бронирований:', {
        startInterval: booking.startInterval,
        endInterval: booking.endInterval,
        booking: booking,
      });

      // Проверяем, что время выбрано (может быть 0, поэтому проверяем строго на null/undefined)
      const hasStartTime = booking.startInterval !== null && booking.startInterval !== undefined;
      const hasEndTime = booking.endInterval !== null && booking.endInterval !== undefined;
      
      if (!hasStartTime || !hasEndTime) {
        alert('Для создания повторяющегося бронирования необходимо выбрать время начала и окончания в полях "С" и "До" в верхней части формы');
        return;
      }

      if (booking.startInterval >= booking.endInterval) {
        alert('Время окончания должно быть позже времени начала');
        return;
      }

      // Определяем тип разделения
      let typeValue = 0; // full
      if (currentWorkingHoursTypes === 'half') typeValue = 1;
      else if (currentWorkingHoursTypes === 'third') typeValue = 2;

      // Определяем часть площадки
      let partValue = 0; // All по умолчанию
      const part = this.props.chosenPart;
      if (part === 'left' || part === 'Left') {
        partValue = 1; // Left
      } else if (part === 'right' || part === 'Right') {
        partValue = 2; // Right
      } else if (part === 'middle' || part === 'Middle') {
        partValue = 4; // Middle
      }

      // Создаем интервалы
      const intervals = [];
      for (let i = booking.startInterval; i < booking.endInterval; i++) {
        intervals.push({
          interval: i,
          part: partValue,
          type: typeValue,
        });
      }

      // Преобразуем вид спорта из строки в числовой флаг
      let sportTypeValue = null;
      if (booking.sport) {
        console.log('🔍 [BookingDialog] Преобразование вида спорта:', {
          bookingSport: booking.sport,
          type: typeof booking.sport,
        });
        const sportFlags = sportKeysToFlagsArray([booking.sport]);
        console.log('🔍 [BookingDialog] Преобразованные флаги:', sportFlags);
        if (sportFlags && sportFlags.length > 0) {
          sportTypeValue = sportFlags[0];
          console.log('🔍 [BookingDialog] Используемый sportType:', sportTypeValue);
        }
      } else {
        console.warn('⚠️ [BookingDialog] Вид спорта не выбран (booking.sport отсутствует)');
      }

      // Формируем детали бронирования
      const details = {
        user: booking.details?.user || '',
        phone: booking.details?.phone || '',
        comment: booking.details?.comment || '',
      };

      const request = {
        playgroundId: playgroundId,
        startDate: choosenDay,
        endDate: recurringEndDate,
        dayOfWeek: parseInt(recurringDayOfWeek),
        interval: parseInt(recurringInterval) || 1,
        intervals: intervals,
        type: typeValue,
        userId: this.state.clientUserId || null,
        sportType: sportTypeValue,
        comment: booking.details?.comment || '',
        details: details,
        rentalItems: booking.rentalItems || [],
      };

      console.log('🔍 [BookingDialog] Запрос на создание повторяющихся бронирований:', {
        ...request,
        intervals: `[${intervals.length} интервалов]`,
        sportType: sportTypeValue,
        details: details,
      });

      try {
        const result = await api.createRecurringBookings(request);
        alert(`Создано бронирований: ${result.createdCount || result.CreatedCount || 0}. Пропущено: ${result.skippedCount || 0}`);
        this.onCancel();
        // Перезагружаем данные через callback
        if (this.props.onRecurringCreated) {
          this.props.onRecurringCreated();
        }
      } catch (error) {
        alert(formatBookingErrorForUser(error));
      }
    } else {
      // Обычное бронирование
      this.props.onSave({
        ...booking,
        userId: this.state.clientUserId || null,
      });
    }
  }

  setBooking = () => {
    // Если передан выбранный диапазон, используем его
    const { selectedRange, intervalData } = this.props;
    let startInterval, endInterval;
    
    if (selectedRange && selectedRange.startInterval !== undefined && selectedRange.endInterval !== undefined) {
      startInterval = selectedRange.startInterval;
      endInterval = selectedRange.endInterval;
    } else if (intervalData) {
      startInterval = intervalData.interval;
      endInterval = intervalData.interval + 1;
    } else {
      startInterval = null;
      endInterval = null;
    }
    
    this.setState({
      booking: {
        ...cleanBooking,
        intervalData: this.props.intervalData,
        startInterval,
        endInterval,
        choosenDay: this.props.choosenDay,
        chosenPart: this.props.chosenPart,
      },
      clientKey: '',
      clientUserId: null,
    }, this.onBookingDataChanged);
  }

  setBookingData = (field, value, cb) => this.setState(prevState => ({
    booking: {
      ...prevState.booking,
      [field]: value,
    },
  }), () => cb && cb())

  setSelectIntervals = () => this.setState(() => {
    const {
      afterIntervals,
      beforeIntervals,
    } = getSelectIntervals(this.state.booking, this.props.schedule);
    return {
      afterIntervals,
      beforeIntervals,
    };
  });

  getBookingTime = (times) => {
    if (!times.length) {
      return '';
    }
    const intervals = times.map(time => time.interval);
    const min = Math.min(...intervals);
    const max = Math.max(...intervals);
    return formatPeriod(min, max + 1);
  };

  render() {
    const {
      show, intervalData, choosenDay, classes, currentWorkingHoursTypes, chosenPart,
      loading, deleteBooking,
    } = this.props;

    const data = intervalData &&
    intervalData.values.length &&
    intervalData.values.find(item =>
      item.part === chosenPart && item.bookings && item.bookings.length);
    const {
      beforeIntervals,
      afterIntervals,
      sportOptions,
      booking,
    } = this.state;


    const bookings = data ? data.bookings : [];
    const hasBookings = !!bookings.length;
    return (
      <Dialog 
        open={show}
        maxWidth="sm"
        fullWidth
        className={classes.dialog}
        PaperProps={{
          className: classes.dialogPaper,
        }}
      >
        <DialogTitle className={classes.dialogTitle}>
          {/* Используем intervalData, т.к. кликнули на ячейку, в которой есть текущий интервал */}
          {/* Если ячейка одна, то показываем заголовок */}
          { `${choosenDay}${bookings.length === 1 ? `, ${this.getBookingTime(bookings[0].intervals)}` : ''}`}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {hasBookings ? bookings.map((bookingItem, index) => (
            <div key={bookingItem.id || `booking-${index}`}>
              {bookings.length > 1 ? <Typography type="h2" component="h2"><strong>{this.getBookingTime(bookingItem.intervals)}</strong></Typography> : ''}
              <Typography type="body1" component="div">
                {
                  bookingItem.type === workingHoursTypeEnum.full ? 'Целая площадка' :
                  `${bookingItem.intervals && bookingItem.intervals.length ? workingHourPartType[bookingItem.intervals[0].part] : ''} (${bookingItem.type === workingHoursTypeEnum.half ? '1/2' : ''}${bookingItem.type === workingHoursTypeEnum.third ? '1/3' : ''}) площадки`
                }
                <div>Забронировано</div>
                {bookingItem.sportType && (() => {
                  // Преобразуем числовой флаг в строковый ключ, затем в название
                  const sportKey = sportFlagsToKeys[bookingItem.sportType];
                  const sportName = sportKey ? sportsEnum[sportKey] : null;
                  return sportName ? (
                <div className={classes.pushTop}>
                      <strong>{capitalize(sportName)}</strong>
                </div>
                  ) : null;
                })()}
                {
                  bookingItem.details && (
                  <>
                    <div className={classes.pushTop}>
                      <strong>{bookingItem.details.user}</strong>
                    </div>
                    <div className={classes.pushTop}>
                      <strong>{bookingItem.details.phone}</strong>
                    </div>
                  </>
                )}
                {
                  bookingItem.user && (
                  <>
                    <div className={classes.pushTop}>
                      <strong>{bookingItem.user.name}</strong>
                    </div>
                    <div className={classes.pushTop}>
                      <strong>{bookingItem.user.phone}</strong>
                    </div>
                  </>
                )}
                {bookingItem.details &&
                <div className={classes.pushTop}>
                  {bookingItem.details.comment}
                </div>}
                {/* Отображение дополнительных услуг */}
                {bookingItem.rentalItems && bookingItem.rentalItems.length > 0 && (
                  <div className={classes.pushTop}>
                    <Typography type="subtitle2" style={{ marginBottom: 8, fontWeight: 'bold' }}>
                      Дополнительные услуги:
                    </Typography>
                    {bookingItem.rentalItems.map((rentalItem, idx) => {
                      // Находим информацию об услуге из availableRentalItems или используем только id
                      const itemInfo = this.state.availableRentalItems.find(i => i.id === rentalItem.id);
                      const itemName = itemInfo ? itemInfo.name : `Услуга #${rentalItem.id}`;
                      return (
                        <div key={idx} style={{ marginBottom: 4, fontSize: 14 }}>
                          • {itemName} {rentalItem.quantity > 1 ? `(x${rentalItem.quantity})` : ''}
                        </div>
                      );
                    })}
                  </div>
                )}
                <hr className={classes.hr} />
                <div className={classes.sumTotal}>
                  Сумма:
                  <span className={classes.sumValue}>
                    {bookingItem.price || 0} руб.
                  </span>
                </div>
                {bookings.length > 1 && (
                  <Button
                    color="primary"
                    className={classes.deleteBookingButton}
                    onClick={() => deleteBooking(bookingItem)}
                  >
                    Отменить бронь
                  </Button>)}
              </Typography>
            </div>
          )) : (
            <div>
              {
                [
                  workingHoursTypeEnum.half,
                  workingHoursTypeEnum.third,
                ].includes(currentWorkingHoursTypes) &&
                <Typography type="body1">
                  {workingHourPartType[chosenPart]}: {currentWorkingHoursTypes === workingHoursTypeEnum.half && '1/2' }{currentWorkingHoursTypes === workingHoursTypeEnum.third && '1/3' }
                </Typography>
              }
              <div className={classes.startEndInterval}>
                С
                <Select
                  multi={false}
                  onChange={(option) => {
                    this.setBookingData('startInterval', option.value, this.onBookingDataChanged);
                  }}
                  className={classes.intervalSelect}
                  clearable={false}
                  value={booking.startInterval}
                  placeholder="C"
                  options={beforeIntervals.map(interval => ({
                    value: interval,
                    label: getMomentFromInterval(interval).format('HH:mm'),
                  }))}
                />
                До
                <Select
                  multi={false}
                  value={booking.endInterval}
                  clearable={false}
                  className={classes.intervalSelect}
                  onChange={(option) => {
                    this.setBookingData('endInterval', option.value, this.onBookingDataChanged);
                  }}
                  placeholder="До"
                  options={afterIntervals.map(interval => ({
                    value: interval,
                    label: getMomentFromInterval(interval).format('HH:mm'),
                  }))}
                />
              </div>
              <div style={{ marginTop: 16, marginBottom: 16 }}>
              <Select
                multi={false}
                value={booking.sport}
                onChange={option => this.setBookingData('sport', option.value)}
                placeholder="Вид спорта"
                options={sportOptions}
                />
              </div>
              <ComplexClientPicker
                selectId="booking-dialog-client"
                options={this.state.orgClientOptions || []}
                value={this.state.clientKey}
                disabled={!!this.state.orgClientsLoading}
                loading={!!this.state.orgClientsLoading}
                orgId={this.state.organizationId}
                emptyText="Нет клиентов организации. Добавьте карточку."
                onCreated={() => this.loadOrgClients()}
                onChange={(key, opt) => {
                  this.setState({
                    clientKey: key || '',
                    clientUserId: opt && opt.userId ? opt.userId : null,
                  });
                  this.setBookingData('details', {
                    user: opt ? (opt.name || '') : '',
                    phone: opt ? (opt.phone || '') : '',
                    comment: this.state.booking.details?.comment || '',
                  });
                }}
              />
              {this.state.booking.details?.user ? (
                <Typography variant="caption" color="textSecondary">
                  {this.state.booking.details.user}
                  {this.state.booking.details.phone ? ` · ${this.state.booking.details.phone}` : ''}
                </Typography>
              ) : null}
              <TextField
                  fullWidth
                label="Комментарий бронирования"
                value={booking.details?.comment || ''}
                onChange={e => this.setBookingData('details', {
                    comment: e.target.value,
                  phone: booking.details?.phone || '',
                  user: booking.details?.user || '',
                })}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                className={classes.field}
              />
              {this.state.availableRentalItems.length > 0 && (
                <div className={classes.pushTop}>
                  <Typography type="subtitle2" style={{ marginBottom: 10, fontWeight: 'bold' }}>
                    Дополнительные услуги
                  </Typography>
                  {this.state.availableRentalItems.map(item => {
                    const isSelected = booking.rentalItems?.some(ri => ri.id === item.id);
                    const selectedItem = booking.rentalItems?.find(ri => ri.id === item.id);
                    const pricingLabel = item.pricingType === 0 
                      ? `Фиксированно: ${item.price} руб.` 
                      : `За час: ${item.price} руб.`;
                    
                    return (
                      <div key={item.id} style={{ 
                        marginBottom: 16, 
                        padding: 16, 
                        border: `1px solid ${isSelected ? '#1976d2' : '#e0e0e0'}`, 
                        borderRadius: 8,
                        backgroundColor: isSelected ? '#f0f7ff' : '#ffffff',
                        transition: 'all 0.2s ease',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: isSelected ? 8 : 0 }}>
                          <input
                            type="checkbox"
                            checked={isSelected || false}
                            onChange={(e) => this.handleRentalItemToggle(item.id, e.target.checked)}
                            style={{ marginRight: 12, width: 18, height: 18, cursor: 'pointer' }}
                          />
                          <div style={{ flex: 1 }}>
                            <Typography type="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                              {item.name}
                            </Typography>
                            <Typography type="caption" style={{ color: '#666', display: 'block' }}>
                              {pricingLabel}
                            </Typography>
                            {item.description && (
                              <Typography type="caption" style={{ color: '#999', display: 'block', marginTop: 4 }}>
                                {item.description}
                              </Typography>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <div style={{ marginLeft: 30, marginTop: 8 }}>
                            <Typography type="caption" style={{ marginRight: 8, fontWeight: 500 }}>Количество:</Typography>
                            <TextField
                              type="number"
                              value={selectedItem?.quantity || 1}
                              onChange={(e) => this.handleRentalItemQuantityChange(item.id, e.target.value)}
                              inputProps={{ min: 1, style: { textAlign: 'center' } }}
                              variant="outlined"
                              size="small"
                              style={{ width: 80 }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <Typography type="body1" className={classes.sumTotal}>
                Сумма:
                <span className={classes.sumValue}>
                  {booking.price || 0} руб.
                </span>
              </Typography>
              
              {/* Опция повторяющегося бронирования */}
              <div className={classes.pushTop} style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #e0e0e0' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={this.state.isRecurring}
                      onChange={(e) => this.setState({ isRecurring: e.target.checked })}
                    />
                  }
                  label="Повторяющееся бронирование"
                />
                
                {this.state.isRecurring && (
                  <div style={{ marginTop: 16, marginLeft: 32 }}>
                    <TextField
                      fullWidth
                      label="Дата окончания"
                      type="date"
                      value={this.state.recurringEndDate}
                      onChange={(e) => this.setState({ recurringEndDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      style={{ marginBottom: 16 }}
                    />
                    
                    <FormControl fullWidth style={{ marginBottom: 16 }}>
                      <InputLabel>День недели</InputLabel>
                      <MuiSelect
                        value={this.state.recurringDayOfWeek}
                        onChange={(e) => this.setState({ recurringDayOfWeek: e.target.value })}
                      >
                        <MenuItem value={0}>Воскресенье</MenuItem>
                        <MenuItem value={1}>Понедельник</MenuItem>
                        <MenuItem value={2}>Вторник</MenuItem>
                        <MenuItem value={3}>Среда</MenuItem>
                        <MenuItem value={4}>Четверг</MenuItem>
                        <MenuItem value={5}>Пятница</MenuItem>
                        <MenuItem value={6}>Суббота</MenuItem>
                      </MuiSelect>
                    </FormControl>
                    
                    <TextField
                      fullWidth
                      label="Каждые N недель"
                      type="number"
                      value={this.state.recurringInterval}
                      onChange={(e) => this.setState({ recurringInterval: e.target.value })}
                      inputProps={{ min: 1 }}
                      helperText="1 = каждую неделю, 2 = каждые 2 недели, и т.д."
                      style={{ marginBottom: 16 }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          {bookings.length === 1 ? (
            <Button
              color="primary"
              onClick={() => deleteBooking(bookings[0])}
              className={classes.button}
            >
              Отменить бронь
            </Button>
          ) : null}
          {!hasBookings &&
            <Button
              disabled={hasBookings || loading}
              color="primary"
              variant="contained"
              onClick={this.handleSave}
              className={classes.button}
            >
              {this.state.loading && <CircularProgress
                size={19}
                style={styles.circularProgress}
              />}
              {this.state.isRecurring ? 'Создать повторяющиеся' : 'Забронировать'}
            </Button>
          }
          <Button 
            disabled={this.state.loading} 
            onClick={this.onCancel} 
            color="primary"
            variant="outlined"
            className={classes.button}
          >
            Отмена
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

const styles = (theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    },
  },
  dialogPaper: {
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    overflow: 'hidden',
  },
  dialogTitle: {
    padding: theme.spacing(3, 3, 2),
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e9ecef',
    fontWeight: 600,
    fontSize: '1.25rem',
    color: '#212529',
  },
  dialogContent: {
    padding: theme.spacing(3),
    minHeight: 200,
    '& .MuiTypography-body1': {
      marginBottom: theme.spacing(1),
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: '#f8f9fa',
    borderTop: '1px solid #e9ecef',
    gap: theme.spacing(1),
  },
  circularProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  pushTop: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    border: '1px solid #e9ecef',
  },
  startEndInterval: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
  sumTotal: {
    display: 'flex',
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: '#e3f2fd',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: '1.1rem',
    alignItems: 'center',
  },
  sumValue: {
    marginLeft: 'auto',
    color: '#1976d2',
    fontSize: '1.2rem',
  },
  hr: {
    height: 1,
    margin: theme.spacing(2, 0),
    padding: 0,
    border: 'none',
    backgroundColor: '#e9ecef',
  },
  intervalSelect: {
    width: '100px',
    '& .Select-control': {
      marginTop: 0,
      borderRadius: 8,
      border: '1px solid #ced4da',
      '&:hover': {
        borderColor: '#1976d2',
      },
    },
    '& .Select-menu-outer': {
      borderRadius: 8,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
  },
  deleteBookingButton: {
    width: '100%',
    borderRadius: 8,
    padding: theme.spacing(1.25, 3),
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginTop: theme.spacing(2),
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  button: {
    borderRadius: 8,
    padding: theme.spacing(1.25, 3),
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
    '&:disabled': {
      opacity: 0.6,
    },
  },
  field: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#1976d2',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: 2,
      },
    },
  },
});

const Styled = withStyles(styles)(BookingDialog);
export default connect(state => ({
  sports: state.sports,
}))(Styled);
